/**
 * src/components/mobile/MobileWorkOrders.jsx
 * Route: /mobile/work-orders
 * Features: WO list → tap → detail bottom sheet → Update Status modal
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MobileLayout from './MobileLayout';
import toast from 'react-hot-toast';

const API  = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('iassets_token')}` });

const TYPE_CFG = {
  breakdown:   { bg:'#fff1f2', color:'#dc2626', border:'#fecaca' },
  pm:          { bg:'#eff6ff', color:'#1a56db', border:'#bfdbfe' },
  inspection:  { bg:'#f0fdf4', color:'#16a34a', border:'#bbf7d0' },
  repair:      { bg:'#fef9c3', color:'#ca8a04', border:'#fde68a' },
  calibration: { bg:'#faf5ff', color:'#7c3aed', border:'#ddd6fe' },
};
const STAT_CFG = {
  open:        { label:'open',        bg:'#f8fafc', color:'#374151', border:'#d1d5db' },
  in_progress: { label:'in progress', bg:'#eff6ff', color:'#1a56db', border:'#bfdbfe' },
  on_hold:     { label:'on hold',     bg:'#fef3c7', color:'#d97706', border:'#fde68a' },
  completed:   { label:'completed',   bg:'#f0fdf4', color:'#16a34a', border:'#bbf7d0' },
  cancelled:   { label:'cancelled',   bg:'#f8fafc', color:'#94a3b8', border:'#e2e8f0' },
};
const tCfg  = t => TYPE_CFG[(t||'').toLowerCase()] || { bg:'#f8fafc', color:'#64748b', border:'#e2e8f0' };
const sCfg  = s => STAT_CFG[(s||'').toLowerCase().replace(/[\s-]/g,'_')] || STAT_CFG.open;
const isOverdue = (due, status) => {
  if (!due || ['completed','cancelled'].includes((status||'').toLowerCase())) return false;
  return new Date(due) < new Date();
};
const fdate   = s => s ? new Date(s).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtCost = n => (n != null && n !== '') ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
const woNum   = wo => wo.wo_number || `WO-${String(wo.id).padStart(4,'0')}`;
const woTitle = wo => wo.title || `${(wo.type||'').replace(/\b\w/g,c=>c.toUpperCase())} — ${wo.asset_code||''}`;

const IS = { width:'100%', border:'1.5px solid #e2e8f0', borderRadius:9,
  padding:'10px 13px', fontSize:14, outline:'none', boxSizing:'border-box',
  background:'#fff', color:'#1a1f2e', WebkitAppearance:'none' };

function groupByDate(wos) {
  const g = {};
  wos.forEach(wo => {
    const d = wo.created_at
      ? new Date(wo.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})
      : 'Unknown';
    if (!g[d]) g[d] = [];
    g[d].push(wo);
  });
  return g;
}

export default function MobileWorkOrders() {
  const [sp]     = useSearchParams();
  const navigate = useNavigate();

  const [wos,        setWos]        = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState(sp.get('type') || 'All');
  const [selWO,      setSelWO]      = useState(null);
  const [showStatus, setShowStatus] = useState(false);
  const [statusForm, setStatusForm] = useState({ status:'open', actual_cost:'0', notes:'' });
  const [saving,     setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/work-orders/?limit=200`, { headers: auth() });
      setWos(r.data?.data || r.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = wo => {
    setSelWO(wo);
    setStatusForm({ status: wo.status || 'open', actual_cost: wo.actual_cost || '0', notes: '' });
    setShowStatus(false);
  };

  const submitStatus = async () => {
    if (!selWO) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/work-orders/${selWO.id}/status`,
        { status: statusForm.status, actual_cost: parseFloat(statusForm.actual_cost)||0, notes: statusForm.notes },
        { headers: auth() }
      );
      toast.success('Status updated');
      const updated = { ...selWO, status: statusForm.status, actual_cost: statusForm.actual_cost };
      setWos(prev => prev.map(w => w.id === selWO.id ? updated : w));
      setSelWO(updated);
      setShowStatus(false);
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const TYPES   = ['All','breakdown','pm','inspection','repair','calibration'];
  const filtered = filter === 'All' ? wos : wos.filter(w => w.type === filter);
  const grouped  = groupByDate([...filtered].sort((a,b) => new Date(b.created_at)-new Date(a.created_at)));

  return (
    <MobileLayout active="workorders" showBack title="Work Orders">

      {/* Filter bar */}
      <div style={{ padding:'10px 14px', background:'#fff', borderBottom:'1px solid #f1f5f9' }}>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ ...IS, padding:'8px 12px' }}>
          {TYPES.map(t => (
            <option key={t} value={t}>
              {t === 'All' ? 'All Types' : t.charAt(0).toUpperCase()+t.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* WO List */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:13 }}>Loading work orders…</div>
      ) : (
        <div>
          {Object.entries(grouped).map(([date, list]) => (
            <div key={date}>
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px 6px' }}>
                <div style={{ flex:1, height:1, background:'#e2e8f0' }}/>
                <span style={{ fontSize:11, fontWeight:600, color:'#64748b', background:'#f0f2f5',
                  padding:'3px 10px', borderRadius:10, border:'1px solid #e2e8f0' }}>{date}</span>
                <div style={{ flex:1, height:1, background:'#e2e8f0' }}/>
              </div>
              {list.map(wo => {
                const tc   = tCfg(wo.type);
                const sc   = sCfg(wo.status);
                const over = isOverdue(wo.due_date, wo.status);
                return (
                  <div key={wo.id} onClick={() => openDetail(wo)}
                    style={{ padding:'12px 14px', background:'#fff',
                      borderBottom:'1px solid #f1f5f9', cursor:'pointer',
                      WebkitTapHighlightColor:'transparent' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                      <span style={{ fontSize:11, fontWeight:700, color:'#64748b', fontFamily:'monospace' }}>{woNum(wo)}</span>
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10,
                        background:tc.bg, color:tc.color, border:`1px solid ${tc.border}` }}>{wo.type||'repair'}</span>
                      <div style={{ flex:1 }}/>
                      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:8,
                        background:sc.bg, color:sc.color, border:`1px solid ${sc.border}` }}>{sc.label}</span>
                    </div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e', lineHeight:1.3, marginBottom:3 }}>
                      {woTitle(wo)}
                    </div>
                    {wo.asset_name && (
                      <div style={{ fontSize:11, color:'#64748b', marginBottom:3 }}>
                        {[wo.asset_name, wo.asset_code].filter(Boolean).join(' — ')}
                      </div>
                    )}
                    {wo.location_name && (
                      <div style={{ fontSize:11, color:'#94a3b8', marginBottom:3 }}>📍 {wo.location_name}</div>
                    )}
                    {wo.due_date && (
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        {over && <span style={{ fontSize:11, fontWeight:700, color:'#dc2626' }}>⚠ Overdue</span>}
                        <span style={{ fontSize:11, color:over?'#dc2626':'#94a3b8' }}>Due: {wo.due_date}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {Object.keys(grouped).length === 0 && (
            <div style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:13 }}>No work orders found.</div>
          )}
        </div>
      )}

      {/* ══ DETAIL BOTTOM SHEET ══ */}
      {selWO && !showStatus && (() => {
        const tc   = tCfg(selWO.type);
        const sc   = sCfg(selWO.status);
        const over = isOverdue(selWO.due_date, selWO.status);
        return (
          <>
            <div onClick={() => setSelWO(null)}
              style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:400, backdropFilter:'blur(2px)' }}/>
            <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:401, background:'#fff',
              borderRadius:'18px 18px 0 0', maxHeight:'88vh', overflowY:'auto',
              boxShadow:'0 -8px 40px rgba(0,0,0,.2)', animation:'slideUp .25s ease' }}>
              <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>

              <div style={{ display:'flex', justifyContent:'center', padding:'10px 0 0' }}>
                <div style={{ width:36, height:4, borderRadius:2, background:'#e2e8f0' }}/>
              </div>

              <div style={{ padding:'12px 16px 28px' }}>
                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'#64748b', fontFamily:'monospace', flex:1 }}>{woNum(selWO)}</span>
                  <span style={{ fontSize:12, fontWeight:600, padding:'4px 12px', borderRadius:8,
                    background:sc.bg, color:sc.color, border:`1px solid ${sc.border}` }}>{sc.label}</span>
                  <button onClick={() => setSelWO(null)}
                    style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#94a3b8', padding:4, lineHeight:1 }}>✕</button>
                </div>

                {/* Title */}
                <div style={{ fontSize:16, fontWeight:800, color:'#1a1f2e', lineHeight:1.3, marginBottom:8 }}>
                  {woTitle(selWO)}
                </div>

                {/* Overdue */}
                {over && (
                  <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#fff1f2',
                    border:'1px solid #fecaca', borderRadius:8, padding:'4px 10px', marginBottom:8 }}>
                    <span style={{ fontSize:12 }}>⚠</span>
                    <span style={{ fontSize:12, fontWeight:700, color:'#dc2626' }}>Overdue</span>
                  </div>
                )}

                {/* Description */}
                {selWO.description && (
                  <div style={{ fontSize:13, color:'#64748b', lineHeight:1.6, marginBottom:10 }}>
                    {selWO.description}
                  </div>
                )}

                {/* Type + Priority tags */}
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
                  {selWO.type && (
                    <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:10,
                      background:tc.bg, color:tc.color, border:`1px solid ${tc.border}` }}>{selWO.type}</span>
                  )}
                  {selWO.priority && (
                    <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:10,
                      background:'#fef9c3', color:'#ca8a04', border:'1px solid #fde68a' }}>{selWO.priority}</span>
                  )}
                </div>

                {/* CTA buttons */}
                <div style={{ display:'flex', gap:10, marginBottom:18 }}>
                  <button onClick={() => setShowStatus(true)}
                    style={{ flex:2, padding:'13px', background:'#1a56db', color:'#fff', border:'none',
                      borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer',
                      display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    ↻ Update Status
                  </button>
                 {/*  <button onClick={() => { setSelWO(null); navigate(`/work-orders/${selWO.id}`); }}
                    style={{ flex:1, padding:'13px', background:'#16a34a', color:'#fff', border:'none',
                      borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer',
                      display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                    View Full →
                  </button> */}
                </div>

                {/* WO Details */}
                <div style={{ fontSize:11, fontWeight:800, color:'#94a3b8',
                  letterSpacing:'.08em', textTransform:'uppercase', marginBottom:10 }}>
                  Work Order Details
                </div>
                <div style={{ border:'1px solid #f1f5f9', borderRadius:10, overflow:'hidden' }}>
                  {[
                    ['Asset',          selWO.asset_name],
                    ['Asset Code',     selWO.asset_code],
                    ['Location',       selWO.location_name],
                    ['Assigned To',    selWO.assigned_to_name || selWO.assigned_to],
                    ['Due Date',       selWO.due_date],
                    ['Estimated Cost', fmtCost(selWO.estimated_cost)],
                    ['Actual Cost',    fmtCost(selWO.actual_cost)],
                    ['Created',        fdate(selWO.created_at)],
                  ].filter(([,v]) => v != null && v !== '' && v !== '—').map(([label, value]) => (
                    <div key={label} style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'center', padding:'10px 14px', borderBottom:'1px solid #f8fafc' }}>
                      <span style={{ fontSize:12, color:'#94a3b8' }}>{label}</span>
                      <span style={{ fontSize:13, fontWeight:700, color:'#1a1f2e', textAlign:'right', maxWidth:'60%' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* ══ UPDATE STATUS MODAL ══ */}
      {showStatus && selWO && (
        <>
          <div onClick={() => setShowStatus(false)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:500 }}/>
          <div style={{ position:'fixed', inset:'auto 0 0', zIndex:501, background:'#fff',
            borderRadius:'18px 18px 0 0', padding:'20px 18px 36px',
            boxShadow:'0 -8px 40px rgba(0,0,0,.25)', animation:'slideUp .2s ease' }}>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <span style={{ fontSize:17, fontWeight:800, color:'#1a1f2e' }}>Update Status</span>
              <button onClick={() => setShowStatus(false)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#94a3b8', padding:4, lineHeight:1 }}>✕</button>
            </div>

            {/* New Status */}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#475569', display:'block',
                marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>New Status</label>
              <select value={statusForm.status}
                onChange={e => setStatusForm(p => ({ ...p, status: e.target.value }))}
                style={{ ...IS }}>
                {[
                  { v:'open',        l:'Open'        },
                  { v:'assigned',        l:'Assigned'        },
                  { v:'in_progress', l:'In Progress' },
                  { v:'on_hold',     l:'On Hold'     },
                  { v:'closed',   l:'Closed'   },
                  { v:'completed',   l:'Completed'   },
                  { v:'cancelled',   l:'Cancelled'   },
                ].map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>

            {/* Actual Cost */}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#475569', display:'block',
                marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>Actual Cost (₹)</label>
              <input type="number" value={statusForm.actual_cost} placeholder="0"
                onChange={e => setStatusForm(p => ({ ...p, actual_cost: e.target.value }))}
                style={{ ...IS }}/>
            </div>

            {/* Notes */}
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#475569', display:'block',
                marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>Notes</label>
              <textarea value={statusForm.notes} placeholder="Update notes…" rows={3}
                onChange={e => setStatusForm(p => ({ ...p, notes: e.target.value }))}
                style={{ ...IS, resize:'vertical', fontFamily:'inherit' }}/>
            </div>

            {/* Buttons */}
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setShowStatus(false)}
                style={{ flex:1, padding:'13px', background:'#f8fafc', color:'#64748b',
                  border:'1px solid #e2e8f0', borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={submitStatus} disabled={saving}
                style={{ flex:2, padding:'13px', background:saving?'#93c5fd':'#1a56db',
                  color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:700,
                  cursor:saving?'not-allowed':'pointer' }}>
                {saving ? 'Updating…' : 'Update Status'}
              </button>
            </div>
          </div>
        </>
      )}
    </MobileLayout>
  );
}
